import React from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import artieImage from "../../assets/images/artie.png";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useParams } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { ArtieScoreData } from "@/types/artie-score";

interface ScoreStatusResponse {
  score_data: ArtieScoreData;
}

const ScoreV2Page: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();

  const { data: scoreStatus, isLoading, error } = useQuery<ScoreStatusResponse>({
    queryKey: ['artieScoreStatus', projectId],
    queryFn: () => {
      if (!projectId) {
        throw new Error("Project ID is missing");
      }
      return apiClient.getArtieScoreStatus(projectId) as Promise<ScoreStatusResponse>;
    },
    enabled: !!projectId,
  });

  const artieScore = scoreStatus?.score_data;
  const psv = artieScore?.psv_calculation;
  const currentScore = artieScore?.artie_score ?? 0;

  const getScoreLabel = (score: number) => {
    if (score > 70) return "Marketplace Ready";
    if (score > 25) return "On Track";
    return "Early Stage";
  }

  const calculateProgress = (score: number) => {
    if (score <= 25) {
      // First segment (0-25 score maps to 0-25% width)
      return score;
    } else if (score <= 70) {
      // Second segment (26-70 score maps to 25-75% width)
      // Range is 45 (70-25), Width is 50%
      return 25 + ((score - 25) / 45) * 50;
    } else {
      // Third segment (71-100 score maps to 75-100% width)
      // Range is 30 (100-70), Width is 25%
      return 75 + ((score - 70) / 30) * 25;
    }
  };

  const progressPercentage = calculateProgress(currentScore);

  return (
    <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">
      <main className="relative z-10 w-full flex flex-col items-center min-h-screen">
        <div className="w-full max-w-5xl px-6 py-12 flex flex-col items-center flex-grow">
          {/* Header & Introduction */}
          <div className="flex flex-col items-center text-center max-w-3xl mb-12">
            <div className="mb-8 relative">
              <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
              <img
                src={artieImage}
                alt="Artie"
                className="w-48 h-48 object-contain relative z-10"
              />
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-cabinet tracking-tight text-[oklch(0.2686_0_0)]">
              There are opportunities to strengthen this project.
            </h1>

            <div className="space-y-1 text-[oklch(0.55_0_0)] text-lg leading-relaxed font-ibm max-w-2xl">
              <p>
                Based on your goal and a review of your script, this project is
                in a solid position — with a few areas that could meaningfully
                improve your outcome.
              </p>
            </div>
          </div>

          {/* Artie Score Card */}
          <div className="w-full max-w-4xl bg-white rounded-[2rem] p-8 md:p-12 mb-12 border border-gray-100 shadow-sm relative overflow-visible">
            {isLoading && <Skeleton className="h-64" />}
            {error && <p className="text-red-500">Error: {error.message}</p>}

            {!isLoading && !error && (
              <>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-start mb-24">
                  <h2 className="text-2xl font-bold text-black tracking-tight">
                    Artie Score
                  </h2>
                  <div className="text-base text-gray-600 text-right mt-2 sm:mt-0 flex flex-col sm:items-end gap-1 font-normal">
                    <span>Budget Goal: ${artieScore?.budget_target?.toLocaleString() ?? '0'}</span>
                    <span>Potential Sponsorship Value: {psv && psv.psv_final ? `$${psv.psv_final.toLocaleString()}` : 'TBD'}</span>
                  </div>
                </div>

                <div className="relative mb-12">
                  <div className="h-4 w-full rounded-full flex gap-1 bg-gray-100">
                    {/* First Segment: 0-25 */}
                    <div className="w-[25%] h-full rounded-l-full relative overflow-hidden bg-gray-100">
                       <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-yellow-300 to-lime-400" style={{ width: `${Math.min((currentScore / 25) * 100, 100)}%` }}></div>
                    </div>
                    
                    {/* Second Segment: 26-70 */}
                    <div className="w-[50%] h-full relative overflow-hidden bg-gray-100">
                      {currentScore > 25 && (
                        <div className="absolute inset-y-0 left-0 bg-lime-400" style={{ width: `${Math.min(((currentScore - 25) / 45) * 100, 100)}%` }}></div>
                      )}
                    </div>

                    {/* Third Segment: 71-100 */}
                    <div className="w-[25%] h-full rounded-r-full relative overflow-hidden bg-gray-100">
                      {currentScore > 70 && (
                        <div className="absolute inset-y-0 left-0 bg-lime-400" style={{ width: `${Math.min(((currentScore - 70) / 30) * 100, 100)}%` }}></div>
                      )}
                    </div>
                  </div>

                  <div className="flex w-full text-[10px] md:text-xs font-medium text-gray-400 mt-4 uppercase tracking-widest px-1">
                    <span className={`w-[25%] text-center ${getScoreLabel(currentScore) === 'Early Stage' ? 'text-black font-bold' : ''}`}>Early Stage</span>
                    <span className={`w-[50%] text-center ${getScoreLabel(currentScore) === 'On Track' ? 'text-black font-bold' : ''}`}>
                      ON TRACK
                    </span>
                    <span className={`w-[25%] text-center ${getScoreLabel(currentScore) === 'Marketplace Ready' ? 'text-black font-bold' : ''}`}>
                      Marketplace Ready ({">"}70)
                    </span>
                  </div>

                  <div className="absolute top-0 -translate-x-1/2 -translate-y-[calc(100%-16px)] flex flex-col items-center z-20" style={{ left: `${progressPercentage}%` }}>
                    <div className="w-20 h-20 bg-black rounded-full flex items-center justify-center shadow-2xl relative z-20 mb-[-1px]">
                      <span className="text-[#a3e635] text-4xl font-bold tracking-tighter">
                        {currentScore}
                      </span>
                    </div>
                    <div className="-mt-8 relative z-10 flex flex-col items-center pointer-events-none">
                      <svg width="60" height="50" viewBox="0 0 60 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-black fill-current">
                        <path d="M10 0 Q 30 45 30 45 Q 30 45 50 0 Z" />
                      </svg>
                      <div className="w-4 h-4 bg-black rounded-full mt-[-7px]"></div>
                    </div>
                  </div>
                </div>

                <div className="text-left">
                  <p className="text-gray-600 text-lg">
                    When your project reaches a{" "}
                    <span className="font-bold text-gray-800">
                      Marketplace Ready
                    </span>
                    , we can introduce it to brands.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="w-full mt-auto pb-10 px-6 md:px-20">
          <div className="w-full border-t border-[oklch(0.92_0_0)] mb-8" />
          <div className="w-full flex items-center justify-between">
            <div className="flex-1 flex justify-start">
              <Button
                variant="ghost"
                className="text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:bg-transparent pl-0"
                onClick={() => window.history.back()}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>

            <div className="flex-1 flex justify-end">
              <Button
                className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => {
                  // Don't mark welcome flow as seen yet - that should happen after the task flow
                  // Navigate to the one-by-one task recommendations page
                  window.location.href = `/project-v2/task-page/${projectId}`;
                }}
                disabled={isLoading}
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ScoreV2Page;
